import React, { createContext, useEffect, useState } from "react";

import { RateStackResponse } from "@eave/shared/types/api";
import { thereWasAProblem } from "@eave/shared/utils/alerts";

import Spinner from "web_modules/shared/Spinner";

import { getRateStack, setPriceDelta } from "./api";

interface IPointsAndCreditsContext {
  busy: boolean;
  rateStack: RateStackResponse | null;
  selectedPriceDelta: number | null;
  setSelectedPriceDelta(delta: number): void;
  saveSelectedPriceDelta(): void;
}

export const PointsAndCreditsContext = createContext<IPointsAndCreditsContext>({
  busy: false,
  rateStack: null,
  selectedPriceDelta: null,
  setSelectedPriceDelta: (_: number) => {},
  saveSelectedPriceDelta: () => {},
});

const PointsAndCreditsProvider = ({ appId, reloadApp, children }) => {
  const [loading, setLoading] = useState<boolean>(true); // spinner for whole-page loads/reloads
  const [busy, setBusy] = useState<boolean>(false); // UI action lock while API request in flight

  const [rateStack, setRateStack] = useState<RateStackResponse | null>(null);
  const [selectedPriceDelta, setSelectedPriceDelta] = useState<number | null>(null);

  const load = () =>
    getRateStack(appId).then(newRateStack => {
      setRateStack(newRateStack);

      const selected = newRateStack.rate_stack.find(r => r.selected);
      if (selected) {
        setSelectedPriceDelta(selected.price_delta);
      }
    });

  useEffect(() => {
    setLoading(true);

    load()
      .catch(e => {
        thereWasAProblem("loading this page");
        throw e;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appId]);

  const saveSelectedPriceDelta = async () => {
    setBusy(true);

    try {
      await setPriceDelta(appId, selectedPriceDelta);
      await load();
    } catch (e) {
      thereWasAProblem("selecting this rate");
      throw e;
    } finally {
      setBusy(false);
    }

    // NOTE: jjw: reloading the loan app here is not necessary for anything on this screen, but is
    // done as a convenience in case someone immediately clicks into another screen where the info
    // would need to be reflected (e.g., cap markets borrower rate lock) - without doing this manual
    // reload of the loan app, the rates, etc., there would be outdated after this screen saves
    reloadApp();
  };

  if (loading) {
    return <Spinner active />;
  }

  return (
    <PointsAndCreditsContext.Provider
      value={{
        busy,
        rateStack,
        selectedPriceDelta,
        setSelectedPriceDelta,
        saveSelectedPriceDelta,
      }}
    >
      {children}
    </PointsAndCreditsContext.Provider>
  );
};

export default PointsAndCreditsProvider;
